import {
  CreateMessageTemplateCategoryDto,
  CreateMessageTemplateCategorySchema,
  MessageTemplateCategoryAdminDto,
  UpdateMessageTemplateCategorySchema,
} from '@act/shared/data-transfer-objects';
import { useForm } from 'react-hook-form';
import { messageTemplatesRequests } from 'redux/requests/message-templates.requests';
import { useApiCall } from 'redux/utils/api-hook';
import { StageKey } from '@act/shared/definitions';
import { useEffect, useMemo } from 'react';
import {
  createMessageTemplateCategorySuccess,
  updateMessageTemplateCategorySuccess,
} from 'redux/slices/messageTemplatesSlice';
import { useAppDispatch } from 'redux/store-hooks';
import { zodResolver } from '@hookform/resolvers/zod';
import { useToast } from '../../../../app/ReusableToast';
import useUnsavedChangesDialog from 'react-components/dialog-forms/hooks/use-unsaved-changes-dialog';
import { FormConfig } from 'stories/forms/FormBuilder/FormBuilder.types';
import { AdminDrawerForm } from '../../AdminDrawer';
import { FormBuilder } from '@act/storybook';

interface Props {
  open: boolean;
  setOpen: (value: boolean) => void;
  isEdit: boolean;
  selectedCategory?: MessageTemplateCategoryAdminDto;
}

export const MessageCategoriesCreateEdit = ({
  open,
  setOpen,
  isEdit,
  selectedCategory,
}: Props) => {
  const toast = useToast();
  const dispatch = useAppDispatch();
  const handleUnsavedChanges = useUnsavedChangesDialog();
  const schema = isEdit
    ? UpdateMessageTemplateCategorySchema
    : CreateMessageTemplateCategorySchema;

  const { control, handleSubmit, formState } =
    useForm<CreateMessageTemplateCategoryDto>({
      defaultValues: selectedCategory || {
        name: '',
        sortOrder: null,
        stageKey: undefined,
      },
      resolver: zodResolver(schema),
    });

  const [
    createMessageTemplateCategoryResult,
    _createMessageTemplateCategoryLoading,
    createMessageTemplateCategory,
  ] = useApiCall(messageTemplatesRequests.createMessageTemplateCategory);
  const [
    updateMessageTemplateCategoryResult,
    _updateMessageTemplateCategoryLoading,
    updateMessageTemplateCategory,
  ] = useApiCall(messageTemplatesRequests.updateMessageTemplateCategory);

  const formConfig: FormConfig<CreateMessageTemplateCategoryDto> = [
    {
      type: 'input',
      name: 'name',
      label: 'Category Name',
      input: 'text',
    },
    {
      type: 'input',
      name: 'stageKey',
      label: 'Stage',
      input: 'select',
      props: {
        options: Object.values(StageKey).map(stage => ({
          label: stage,
          value: stage,
        })),
        fullWidth: true,
      },
    },
    {
      type: 'input',
      name: 'sortOrder',
      label: 'Sort Order',
      input: 'number',
    },
  ];

  const onSubmit = async (data: CreateMessageTemplateCategoryDto) => {
    if (isEdit) {
      void updateMessageTemplateCategory({
        params: { id: Number(selectedCategory?.id) },
        body: {
          name: data.name,
          sortOrder: Number(data.sortOrder),
          stageKey: data.stageKey as StageKey,
        },
      });
    } else {
      void createMessageTemplateCategory({
        params: null,
        body: {
          name: data.name,
          sortOrder: Number(data.sortOrder),
          stageKey: data.stageKey as StageKey,
        },
      });
    }
  };

  useEffect(() => {
    if (
      createMessageTemplateCategoryResult ||
      updateMessageTemplateCategoryResult
    ) {
      if (createMessageTemplateCategoryResult) {
        dispatch(
          createMessageTemplateCategorySuccess(
            createMessageTemplateCategoryResult
          )
        );
        toast('Message template category created successfully');
      }
      if (updateMessageTemplateCategoryResult) {
        dispatch(
          updateMessageTemplateCategorySuccess(
            updateMessageTemplateCategoryResult
          )
        );
        toast('Message template category updated successfully');
      }
      setOpen(false);
    }
  }, [
    createMessageTemplateCategoryResult,
    updateMessageTemplateCategoryResult,
  ]);
  const { isDirty } = formState;

  const handleClose = () => {
    if (isDirty) {
      void handleUnsavedChanges(() => {
        setOpen(false);
      });
    } else {
      setOpen(false);
    }
  };

  const DrawerContent = useMemo(() => {
    return (
      <FormBuilder
        config={formConfig}
        control={control}
        formState={formState}
      />
    );
  }, [control, formConfig, formState.errors]);
  return (
    <AdminDrawerForm
      title={isEdit ? `Edit Category` : `Add New Category`}
      children={DrawerContent}
      onSubmit={handleSubmit(onSubmit)}
      open={open}
      setOpen={handleClose}
    />
  );
};
